import React, { useState } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes, faSeedling, faLayerGroup, faBug, faPlus, faTrash,
} from "@fortawesome/free-solid-svg-icons";
import ModalAlert from "../reusable/ModalAlert";
import ConfirmBox from "../reusable/ConfirmBox";

const C = {
  dark: "#1a3328", mid: "#2d5a3d", bright: "#3d8c5e",
  light: "#e8f5ee", page: "#f4f6f5", border: "#d8e8dc",
  white: "#ffffff", textDk: "#1a2e22", textGr: "#6c757d",
};

const TABS = [
  { key: "name",       label: "Crop Name",  icon: faSeedling  },
  { key: "categories", label: "Categories", icon: faLayerGroup },
  { key: "problems",   label: "Problems",   icon: faBug        },
];

const inp = {
  height: 38, borderRadius: 8, border: `1.5px solid ${C.border}`,
  padding: "0 12px", fontSize: "0.87rem", outline: "none", width: "100%",
  boxSizing: "border-box", color: C.textDk,
};

const trashBtn = {
  width: 32, height: 32, borderRadius: 7, flexShrink: 0,
  border: "none", cursor: "pointer",
  backgroundColor: "#fdecea", color: "#c62828",
  display: "flex", alignItems: "center", justifyContent: "center",
};

const CropFormDrawer = ({ action, crop: initCrop, onClose, onSuccess }) => {
  const isEdit = action === "edit";

  const [crop, setCrop]           = useState(
    isEdit ? JSON.parse(JSON.stringify(initCrop || {})) : { crop: "", categories: [] }
  );
  const [activeTab, setActiveTab] = useState("name");
  const [selCatIdx, setSelCatIdx] = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [alert,     setAlert]     = useState(null);
  // { type: 'crop' } | { type: 'category', idx, id, label } | { type: 'problem', catIdx, pIdx, id, label }
  const [confirmDelete, setConfirmDelete] = useState(null);

  /* ── Save ─────────────────────────────────────────────────────────────── */
  const handleSave = () => {
    if (!crop.crop?.trim()) {
      setAlert({ variant: "danger", message: "Crop name is required." });
      return;
    }
    setSaving(true);
    axios
      .post(`${process.env.REACT_APP_API_URL}/api/crop/addEditCrop`, crop)
      .then((res) => {
        if (res.status === 200) {
          // Use the server's copy (has real _ids for anything just created) so a
          // later save doesn't resubmit id-less items and duplicate them.
          setCrop(res.data);
          setAlert({ variant: "success", message: "Crop saved successfully." });
          onSuccess(res.data, { close: false });
        } else {
          setAlert({ variant: "danger", message: "Failed to save crop." });
        }
      })
      .catch(() => {
        setAlert({ variant: "danger", message: "Network error. Please try again." });
      })
      .finally(() => setSaving(false));
  };

  /* ── Category helpers ─────────────────────────────────────────────────── */
  const addCategory = () => {
    const t = JSON.parse(JSON.stringify(crop));
    if (!t.categories) t.categories = [];
    t.categories.push({ category: "", problems: [] });
    setCrop(t);
  };

  const updateCategoryName = (idx, val) => {
    const t = JSON.parse(JSON.stringify(crop));
    t.categories[idx].category = val;
    setCrop(t);
  };

  // Called after confirmed delete (or directly for unsaved items)
  const removeCategoryFromState = (idx) => {
    const t = JSON.parse(JSON.stringify(crop));
    t.categories.splice(idx, 1);
    if (selCatIdx === idx) setSelCatIdx(null);
    else if (selCatIdx > idx) setSelCatIdx(selCatIdx - 1);
    setCrop(t);
    return t;
  };

  const handleCategoryTrash = (idx) => {
    const cat = crop.categories[idx];
    if (!cat._id) {
      // Unsaved — remove from draft directly
      removeCategoryFromState(idx);
      return;
    }
    setConfirmDelete({
      type: "category",
      idx,
      id: cat._id,
      label: cat.category || `Category ${idx + 1}`,
    });
  };

  /* ── Problem helpers ──────────────────────────────────────────────────── */
  const addProblem = (catIdx) => {
    const t = JSON.parse(JSON.stringify(crop));
    t.categories[catIdx].problems.push({ problemText: "" });
    setCrop(t);
  };

  const updateProblem = (catIdx, pIdx, val) => {
    const t = JSON.parse(JSON.stringify(crop));
    t.categories[catIdx].problems[pIdx].problemText = val;
    setCrop(t);
  };

  const removeProblemFromState = (catIdx, pIdx) => {
    const t = JSON.parse(JSON.stringify(crop));
    t.categories[catIdx].problems.splice(pIdx, 1);
    setCrop(t);
    return t;
  };

  const handleProblemTrash = (catIdx, pIdx) => {
    const prob = crop.categories[catIdx].problems[pIdx];
    if (!prob._id) {
      removeProblemFromState(catIdx, pIdx);
      return;
    }
    setConfirmDelete({
      type: "problem",
      catIdx,
      pIdx,
      id: prob._id,
      label: prob.problemText || `Problem ${pIdx + 1}`,
    });
  };

  /* ── Confirm delete action ────────────────────────────────────────────── */
  const executeDelete = async () => {
    if (!confirmDelete) return;
    const { type, id, idx, catIdx, pIdx } = confirmDelete;

    let url;
    if (type === "crop")     url = "/api/crop/deleteCrop";
    if (type === "category") url = "/api/crop/deleteCategory";
    if (type === "problem")  url = "/api/crop/deleteProblem";

    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}${url}`, { _id: id });
      if (res.status === 200) {
        if (type === "crop") {
          onSuccess();
          onClose();
        } else if (type === "category") {
          const updated = removeCategoryFromState(idx);
          // Keep the drawer open on category delete so the next action (e.g.
          // deleting another category, adding a new one) can happen right away.
          onSuccess(updated, { close: false });
        } else if (type === "problem") {
          const updated = removeProblemFromState(catIdx, pIdx);
          // Same as category delete — stay open for the next action.
          onSuccess(updated, { close: false });
        }
      } else {
        setAlert({ variant: "danger", message: `Failed to delete ${type}.` });
      }
    } catch {
      setAlert({ variant: "danger", message: `Network error while deleting ${type}.` });
    }
  };

  /* ── Render ───────────────────────────────────────────────────────────── */
  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.45)", zIndex: 1100 }}
      />

      {/* Panel */}
      <div style={{
        position: "fixed", top: 0, right: 0, bottom: 0,
        width: "min(580px, 96vw)",
        backgroundColor: C.white,
        boxShadow: "-4px 0 24px rgba(0,0,0,0.18)",
        zIndex: 1200,
        display: "flex", flexDirection: "column",
        animation: "cropDrawerIn 0.25s ease",
      }}>
        <style>{`@keyframes cropDrawerIn { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>

        {/* Header */}
        <div style={{
          backgroundColor: C.dark, padding: "1rem 1.5rem", flexShrink: 0,
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              width: 34, height: 34, borderRadius: 8, backgroundColor: C.mid,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <FontAwesomeIcon icon={faSeedling} style={{ color: "#fff", fontSize: "0.85rem" }} />
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: "0.95rem", color: "#fff" }}>
                {crop._id ? "Edit Crop" : "Add Crop"}
              </div>
              <div style={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.5)" }}>
                {crop._id ? `Editing: ${crop.crop || ""}` : "Define crop name, categories & problems"}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {crop._id && (
              <button
                type="button"
                onClick={() => setConfirmDelete({ type: "crop", id: crop._id, label: crop.crop })}
                style={{
                  background: "rgba(220,53,69,0.25)", border: "none", cursor: "pointer",
                  width: 32, height: 32, borderRadius: 8,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: "#ff8a8a",
                }}
                title="Delete Crop"
              >
                <FontAwesomeIcon icon={faTrash} style={{ fontSize: "0.82rem" }} />
              </button>
            )}
            <button onClick={onClose} style={{
              background: "rgba(255,255,255,0.12)", border: "none", cursor: "pointer",
              width: 32, height: 32, borderRadius: 8,
              display: "flex", alignItems: "center", justifyContent: "center", color: "#fff",
            }}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>

        {/* Tab bar */}
        <div style={{
          display: "flex", borderBottom: `2px solid ${C.border}`,
          backgroundColor: "#fafcfb", flexShrink: 0,
        }}>
          {TABS.map(({ key, label, icon }) => {
            const active = activeTab === key;
            return (
              <button key={key} onClick={() => setActiveTab(key)} style={{
                background: "none", border: "none", cursor: "pointer",
                padding: "0.6rem 1.2rem", marginBottom: -2,
                display: "flex", alignItems: "center", gap: 6,
                fontSize: "0.82rem", fontWeight: active ? 700 : 400,
                color: active ? C.mid : C.textGr,
                borderBottom: `2px solid ${active ? C.mid : "transparent"}`,
                transition: "color 0.15s",
              }}>
                <FontAwesomeIcon icon={icon} style={{ fontSize: "0.68rem" }} />
                {label}
              </button>
            );
          })}
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "1.25rem" }}>

          {/* ── Crop Name ── */}
          {activeTab === "name" && (
            <div>
              <label style={{ fontSize: "0.82rem", fontWeight: 600, color: C.textDk, marginBottom: 6, display: "block" }}>
                Crop Name *
              </label>
              <input
                value={crop.crop || ""}
                onChange={(e) => setCrop({ ...crop, crop: e.target.value })}
                placeholder="e.g. Wheat, Sugarcane, Cotton"
                style={{ ...inp, height: 42, fontSize: "0.92rem" }}
              />
              <div style={{ fontSize: "0.75rem", color: C.textGr, marginTop: 8 }}>
                After setting the crop name, go to "Categories" and "Problems" tabs to add details.
              </div>
            </div>
          )}

          {/* ── Categories ── */}
          {activeTab === "categories" && (
            <div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.85rem" }}>
                <div style={{ fontSize: "0.82rem", fontWeight: 600, color: C.textDk }}>
                  Categories ({crop.categories?.length || 0})
                </div>
                <button onClick={addCategory} style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  height: 30, padding: "0 12px", borderRadius: 7,
                  border: "none", cursor: "pointer", fontSize: "0.78rem", fontWeight: 600,
                  backgroundColor: C.mid, color: "#fff",
                }}>
                  <FontAwesomeIcon icon={faPlus} style={{ fontSize: "0.62rem" }} />
                  Add Category
                </button>
              </div>

              {(!crop.categories || crop.categories.length === 0) ? (
                <div style={{
                  textAlign: "center", padding: "3rem", color: C.textGr, fontSize: "0.84rem",
                  border: `1.5px dashed ${C.border}`, borderRadius: 10,
                }}>
                  No categories yet. Click "Add Category" to start.
                </div>
              ) : (
                crop.categories.map((cat, idx) => (
                  <div key={idx} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                    <input
                      value={cat.category}
                      onChange={(e) => updateCategoryName(idx, e.target.value)}
                      placeholder={`Category ${idx + 1}`}
                      style={inp}
                    />
                    <button onClick={() => handleCategoryTrash(idx)} style={trashBtn}>
                      <FontAwesomeIcon icon={faTrash} style={{ fontSize: "0.68rem" }} />
                    </button>
                  </div>
                ))
              )}
            </div>
          )}

          {/* ── Problems ── */}
          {activeTab === "problems" && (
            <div>
              {(!crop.categories || crop.categories.length === 0) ? (
                <div style={{
                  textAlign: "center", padding: "3rem", color: C.textGr, fontSize: "0.84rem",
                  border: `1.5px dashed ${C.border}`, borderRadius: 10,
                }}>
                  Add categories first before adding problems.
                </div>
              ) : (
                <>
                  <div style={{ fontSize: "0.8rem", fontWeight: 600, color: C.textDk, marginBottom: "0.6rem" }}>
                    Select a category:
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: "1.1rem" }}>
                    {crop.categories.map((cat, idx) => (
                      <button
                        key={idx}
                        onClick={() => setSelCatIdx(idx)}
                        style={{
                          padding: "5px 12px", borderRadius: 20, fontSize: "0.78rem", fontWeight: 600,
                          border: `1.5px solid ${selCatIdx === idx ? C.mid : C.border}`,
                          backgroundColor: selCatIdx === idx ? C.light : "#fff",
                          color: selCatIdx === idx ? C.mid : C.textGr,
                          cursor: "pointer",
                        }}
                      >
                        {cat.category || `Category ${idx + 1}`}
                        <span style={{ marginLeft: 5, fontSize: "0.68rem" }}>({cat.problems?.length || 0})</span>
                      </button>
                    ))}
                  </div>

                  {selCatIdx !== null && crop.categories[selCatIdx] && (
                    <div>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.7rem" }}>
                        <div style={{ fontSize: "0.8rem", fontWeight: 600, color: C.mid }}>
                          Problems for: {crop.categories[selCatIdx].category || `Category ${selCatIdx + 1}`}
                        </div>
                        <button onClick={() => addProblem(selCatIdx)} style={{
                          display: "inline-flex", alignItems: "center", gap: 5,
                          height: 28, padding: "0 10px", borderRadius: 7,
                          border: "none", cursor: "pointer", fontSize: "0.75rem", fontWeight: 600,
                          backgroundColor: C.mid, color: "#fff",
                        }}>
                          <FontAwesomeIcon icon={faPlus} style={{ fontSize: "0.6rem" }} />
                          Add Problem
                        </button>
                      </div>

                      {crop.categories[selCatIdx].problems.length === 0 ? (
                        <div style={{
                          textAlign: "center", padding: "1.5rem", color: C.textGr, fontSize: "0.82rem",
                          border: `1.5px dashed ${C.border}`, borderRadius: 8,
                        }}>
                          No problems yet. Click "Add Problem".
                        </div>
                      ) : (
                        crop.categories[selCatIdx].problems.map((prob, pIdx) => (
                          <div key={pIdx} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                            <input
                              value={prob.problemText}
                              onChange={(e) => updateProblem(selCatIdx, pIdx, e.target.value)}
                              placeholder={`Problem ${pIdx + 1}`}
                              style={inp}
                            />
                            <button onClick={() => handleProblemTrash(selCatIdx, pIdx)} style={trashBtn}>
                              <FontAwesomeIcon icon={faTrash} style={{ fontSize: "0.68rem" }} />
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: "0.9rem 1.5rem", borderTop: `1px solid ${C.border}`,
          display: "flex", justifyContent: "flex-end", gap: 10,
          backgroundColor: "#fafcfb", flexShrink: 0,
        }}>
          <button onClick={onClose} style={{
            height: 38, padding: "0 18px", borderRadius: 9,
            border: `1.5px solid ${C.border}`, cursor: "pointer",
            fontSize: "0.87rem", fontWeight: 600, color: C.textGr,
            backgroundColor: C.white,
          }}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving} style={{
            height: 38, padding: "0 22px", borderRadius: 9,
            border: "none", cursor: saving ? "not-allowed" : "pointer",
            fontSize: "0.87rem", fontWeight: 600,
            backgroundColor: saving ? "#a0b8ab" : C.mid, color: "#fff",
          }}>
            {saving ? "Saving…" : "Save Crop"}
          </button>
        </div>
      </div>

      {alert && (
        <ModalAlert
          variant={alert.variant}
          message={alert.message}
          resetCallback={() => setAlert(null)}
        />
      )}

      {confirmDelete && (
        <ConfirmBox
          heading={`Delete ${confirmDelete.type.charAt(0).toUpperCase() + confirmDelete.type.slice(1)}`}
          message={
            confirmDelete.type === "crop"
              ? `Delete crop "${confirmDelete.label}"? This will also delete all its categories and problems. This cannot be undone.`
              : confirmDelete.type === "category"
              ? `Delete category "${confirmDelete.label}"? All problems inside it will also be deleted. This cannot be undone.`
              : `Delete problem "${confirmDelete.label}"? This cannot be undone.`
          }
          confirmAction={executeDelete}
          resetCallback={setConfirmDelete}
        />
      )}
    </>
  );
};

export default CropFormDrawer;
